import { createContext, useContext, useRef, useState } from 'react';
import moment from 'moment-timezone';

import EdenContext from '../edenContext';
import axiosInstance from '../../api/axiosInstance';

const CustomerOutreachContext = createContext();

export const CustomerOutreachProvider = ({ children }) => {
	const { userMail, userType, userInfo } = useContext(EdenContext);

	const [customerSummary, setCustomerSummary] = useState(false);
	const [customerOutreach, setCustomerOutreach] = useState(true);
	const [data, setData] = useState([{}]);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState(null);
	const [sendMailCount, setSendMailCount] = useState(0);
	const [contactInfoUpdate, setContactInfoUpdate] = useState(null);
	const [accounts, setAccounts] = useState([]);
	const [selectedRows, setSelectedRows] = useState([]);
	const abortControllerRef = useRef(null);

	const SINGAPORE_TZ = 'Asia/Singapore';

	const fetchAndParseCSV = async () => {
		try {
			const response = await axiosInstance.get(
				'https://internal.supremecomponents.com/api/threshold_mails.csv',
				{
					responseType: 'blob',
				}
			);

			const text = await response.data.text();
			const rows = text.trim().split('\n');
			return rows.map((row) => {
				const parts = row.split(' --- ');
				return {
					Email: parts[0].replace('Email: ', '').trim().replace(/^"|"$/g, ''),
					ThresholdDate: parts[1]
						.replace('Threshold Date ', '')
						.trim()
						.replace(/^"|"$/g, ''),
					LastActivity: parts[2]
						.replace('Last Activity: ', '')
						.trim()
						.replace(/^"|"$/g, ''),
					DaySlab: Number(
						parts[3]
							.replace('Fall under day slab: ', '')
							.trim()
							.replace(/^"|"$/g, '')
					),
				};
			});
		} catch (error) {
			console.error('Error fetching CSV:', error);
			return [];
		}
	};

	const preprocessData = (data, dateFields) => {
		return data.map((row) => {
			const newRow = { ...row };
			dateFields.forEach((field) => {
				if (row[field]) {
					const dateValue = moment.tz(row[field], SINGAPORE_TZ).startOf('day');
					const today = moment.tz(SINGAPORE_TZ).startOf('day');
					const openDay = today.diff(dateValue, 'days');

					newRow[`${field}_label`] = openDay;
				} else {
					newRow[`${field}_label`] = null;
				}
			});
			return newRow;
		});
	};

	const filterByEmail = (data, emails) => {
		return data.filter((item) =>
			emails
				.map((value) => value.toLowerCase())
				.includes(item.SalesRepEmail?.toLowerCase())
		);
	};

	const getData = async () => {
		setSendMailCount(0);
		setIsLoading(true);
		setSelectedRows([]);

		if (abortControllerRef.current) {
			abortControllerRef.current.abort();
		}
		abortControllerRef.current = new AbortController();
		const signal = abortControllerRef.current.signal;

		const emailFilters =
			userType === 'sr'
				? [userMail]
				: userType === 'sm'
				? userInfo?.EmailFilters?.ViewAsSM
				: userType === 'ss'
				? userInfo?.EmailFilters?.ViewAsSS
				: userType === 'admin' || userType === 'ceo'
				? userInfo?.EmailFilters?.ViewAsAdmin
				: [];

		try {
			const [response1, csvData] = await Promise.all([
				axiosInstance.get(
					`https://internal.supremecomponents.com/api/externalgateway.php?route=${
						customerOutreach
							? 'get_customer_outreach'
							: 'get_customer_outreach_summary'
					}&sales_rep_email=${userMail}&is_admin=${
						userType === 'admin' || userType === 'ceo' ? true : false
					}&emails=${emailFilters.join(',')}`,
					{ signal }
				),
				fetchAndParseCSV(),
			]);

			let processedData = preprocessData(response1.data?.data, [
				'last_incoming_date',
				'last_outgoing_date',
				'last_outgoing_outreach_date',
				'last_automated_outreach_date',
			]);

			const emailMapping = csvData.reduce((acc, item) => {
				acc[item.Email.toLowerCase()] = item.ThresholdDate;
				return acc;
			}, {});

			processedData = processedData.map((row) => ({
				...row,
				ThresholdDate: emailMapping[row.ContactEmail?.toLowerCase()] || null,
			}));

			const filteredData = filterByEmail(processedData, emailFilters);

			setData(filteredData);
			setAccounts([...new Set(filteredData.map((el) => el.Account))]);
			setError(null);
		} catch (err) {
			if (err?.name === 'AbortError' || err?.code === 'ERR_CANCELED') {
				console.log('Request canceled:', err.message || err);
			} else {
				setError(err);
				setData(null);
			}
		} finally {
			setIsLoading(false);
		}
	};

	// const getData = () => {
	// 	setSendMailCount(0);
	// 	setIsLoading(true);
	// 	setSelectedRows([]);

	// 	if (abortControllerRef.current) {
	// 		abortControllerRef.current.abort();
	// 	}
	// 	abortControllerRef.current = new AbortController();
	// 	const signal = abortControllerRef.current.signal;

	// 	const emailFilters =
	// 		userType === 'sr'
	// 			? [userMail]
	// 			: userType === 'sm'
	// 			? userInfo?.EmailFilters?.ViewAsSM
	// 			: userType === 'ss'
	// 			? userInfo?.EmailFilters?.ViewAsSS
	// 			: userType === 'admin' || userType === 'ceo'
	// 			? userInfo?.EmailFilters?.ViewAsAdmin
	// 			: [];

	// 	axiosInstance
	// 		.get(
	// 			`https://api/externalgateway.php?route=${
	// 				customerOutreach
	// 					? 'get_customer_outreach'
	// 					: 'get_customer_outreach_summary'
	// 			}&sales_rep_email=${userMail}&is_admin=${
	// 				userType === 'admin' || userType === 'ceo' ? true : false
	// 			}&emails=${emailFilters.join(',')}`,
	// 			{ signal }
	// 		)
	// 		.then((response) => {
	// 			const processData = preprocessData(response.data?.data, [
	// 				'last_incoming_date',
	// 				'last_outgoing_date',
	// 				'last_outgoing_outreach_date',
	// 				'last_automated_outreach_date',
	// 			]);

	// 			const data = filterByEmail(processData, emailFilters);

	// 			setData(data);

	// 			setAccounts([...new Set(data?.map((el) => el.Account))]);
	// 			setError(null);
	// 			setIsLoading(false);
	// 		})
	// 		.catch((err) => {
	// 			if (axios.isCancel(err)) {
	// 				console.log('Request canceled:', err.message);
	// 			} else {
	// 				setError(err);
	// 				setData(null);
	// 				setIsLoading(false);
	// 			}
	// 		});
	// };

	const showCustomerSummary = () => {
		setCustomerSummary(true);
		setCustomerOutreach(false);
	};

	const showCustomerOutreach = () => {
		setCustomerSummary(false);
		setCustomerOutreach(true);
	};

	return (
		<CustomerOutreachContext.Provider
			value={{
				customerSummary,
				customerOutreach,
				showCustomerSummary,
				showCustomerOutreach,
				getData,
				data,
				setData,
				isLoading,
				error,
				sendMailCount,
				setSendMailCount,
				contactInfoUpdate,
				setContactInfoUpdate,
				accounts,
				selectedRows,
				setSelectedRows,
			}}
		>
			{children}
		</CustomerOutreachContext.Provider>
	);
};

export default CustomerOutreachContext;
